import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Tag, Space, Image, Typography, Divider, Row, Col, Card, Spin, Alert } from 'antd';
import { EyeOutlined, DollarOutlined, ShoppingOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { productService } from '../services/api';
import SmartImage from './SmartImage';

const { Title, Text, Paragraph } = Typography;

// 🔧 NETTOYEUR GLOBAL DE CARACTÈRES CORROMPUS
//   - U+FFFD � = DIAMANT QUESTION (irréversible, écrit avant correction charset MySQL)
//   - espaces multiples / zéro-width parasites
const REPAIR_MAP = (() => {
  const FFD = '\uFFFD';
  const P = (pieces) => pieces.join(FFD);
  const R = (s, flags) => new RegExp(s, flags);
  const pairs = [
    [P(['D','tect']),       'Détect'],
    [P(['d','tect']),       'détect'],
    [P(['monophas']),       'monophasé'],
    [P(['triphas']),        'triphasé'],
    [P(['r','f','rence']),  'référence'],
    [P(['R','f','rence']),  'Référence'],
    [P(['d','crit']),       'décrit'],
    [P(['','quip']),        'équip'],
    [P(['','tabli']),       'établi'],
    [P(['','tabl']),        'établ'],
    [P(['s','curit']),      'sécurité'],
    [P(['S','curit']),      'Sécurité'],
    [P(['gr','ce']),        'grâce'],
    [P(['','nergie']),      'énergie'],
    [P(['','lectrique']),   'électrique'],
    [P(['','lectron']),     'électron'],
    [P(['fum','e']),        'fumée'],
    [P(['Fum','e']),        'Fumée'],
    [P(['qualit']),         'qualité'],
    [P(['autorit']),        'autorité'],
    [P(['optiqu','e']),     'optique'],
    [P(['Optiqu','e']),     'Optique'],
    [P(['ponctuel']),       'ponctuel'],
    [P(['Ponctuel']),       'Ponctuel'],
    [P(['Gamme']),          'Gamme'],
    [P(['Onduleur']),       'Onduleur'],
    [P(['onduleur']),       'onduleur'],
    [P(['Adressable']),     'Adressable'],
    [P(['adressable']),     'adressable'],
    [P(['parfaite']),       'parfaite'],
    [P(['','quip','s']),    'équipés'],
    [P(['d','tecter']),     'détecter'],
    [P(['d','celer']),      'déceler'],
    [P(['pr','sentes']),    'présentes'],
    [P(['r','flexion']),    'réflexion'],
    [P(['particuli','rement']), 'particulièrement'],
    [P(['d','gagement']),   'dégagement'],
    [P(['compos','']),      'composé'],
    [P(['thermique']),      'thermique'],
    [P(['opto-thermique']), 'opto-thermique'],
    [P(['syst','me']),      'système'],
    [P(['s','curit','']),   'sécurité'],
    [P(['g','n','ralement']),'généralement'],
    [P(['employ','']),      'employé'],
    [P(['','tablissements']),'établissements'],
    [P(['galement']),       'également'],
    [P(['id','al']),        'idéal'],
    [P(['d','tection']),    'détection'],
    [P(['combustion']),     'combustion']
  ];
  return pairs.map(([p, r]) => [R(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), r]);
})();

const cleanDisplay = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map(x => cleanDisplay(x));
  if (typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) out[k] = cleanDisplay(v[k]);
    return out;
  }
  let s = String(v);
  if (/\uFFFD/.test(s)) {
    for (const [rx, replacement] of REPAIR_MAP) {
      s = s.replace(rx, replacement);
    }
  }
  s = s.replace(/\uFFFD{2,}/g, ' · ');
  s = s.replace(/\uFFFD/g, '');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
};

const ProductDetailsModal = ({ visible, onClose, productId }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && productId) {
      loadProductDetails();
    }
  }, [visible, productId]);

  const loadProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProductById(productId);
      const raw = response.product;
      setProduct(cleanDisplay(raw));
    } catch (err) {
      console.error('Erreur lors du chargement des détails du produit:', err);
      setError('Erreur lors du chargement des détails du produit');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const normalizeImageUrl = (raw) => {
    if (!raw) return null;
    let url = String(raw);
    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    if (url.startsWith('/uploads')) return `${url}`;
    return `${url}`;
  };

  const normalizeDocumentUrl = (raw) => {
    if (!raw) return null;
    let url = String(raw);
    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    if (url.startsWith('/uploads')) return `${url}`;
    return `${url}`;
  };

  const renderImage = (imageUrl, alt) => {
    return (
      <SmartImage
        src={imageUrl}
        alt={cleanDisplay(alt)}
        // 🔧 FIX: Ne PAS passer width="50%" (string avec % → NaN). Utiliser container CSS.
        width={null}
        height={null}
        className="product-details-image"
        imgStyle={{ objectFit: 'contain', width: '100%', height: 220, borderRadius: 8, border: '1px solid #f0f0f0', background: '#fafafa' }}
        showFallback={true}
      />
    );
  };

  return (
    <Modal
      title={
        <Space>
          <EyeOutlined />
          <span>Détails du produit</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Chargement des détails...</div>
        </div>
      )}

      {error && (
        <Alert
          title="Erreur"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {product && !loading && (
        <div>
          {/* En-tête avec image principale */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              {renderImage(product.images?.main, product.name)}
            </Col>
            <Col xs={24} sm={16}>
              <Title level={3} style={{ marginBottom: 8 }}>
                {product.name}
              </Title>
              <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
                Référence: <Text code>{product.reference}</Text>
              </Text>
              <Space wrap>
                <Tag color={product.status === 'published' ? 'green' : product.status === 'draft' ? 'orange' : 'default'}>
                  {product.status === 'published' ? 'Publié' : product.status === 'draft' ? 'Brouillon' : 'Archivé'}
                </Tag>
                <Tag color={product.isVisible ? 'blue' : 'red'}>
                  {product.isVisible ? 'Visible' : 'Masqué'}
                </Tag>
                {product.isFeatured && <Tag color="gold">Mis en avant</Tag>}
              </Space>
              {product.categories && product.categories.length > 0 && (
                <Space wrap style={{ marginTop: 10 }}>
                  {product.categories.map(category => (
                    <Tag key={category.id || category.name} color="blue">
                      {cleanDisplay(category.name)}
                    </Tag>
                  ))}
                </Space>
              )}
            </Col>
          </Row>

          <Divider />

          {/* Informations générales */}
          <Descriptions title="Informations générales" variant="outlined" column={2} size="small">
            <Descriptions.Item label="Nom" span={2}>
              {product.name}
            </Descriptions.Item>
            <Descriptions.Item label="Référence">
              <Text code>{product.reference}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Slug">
              <Text code>{product.slug}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Description courte" span={2}>
              {product.shortDescription || 'Non renseignée'}
            </Descriptions.Item>
            <Descriptions.Item label="Description complète" span={2}>
              <Paragraph style={{ marginBottom: 0 }}>
                {product.description || 'Non renseignée'}
              </Paragraph>
            </Descriptions.Item>
          </Descriptions>


          {/* Sous-champs spécifiques de la catégorie + Facettes GLOBALES (même si pas dans specificFields) */}
          <Divider />
          <Title level={4}>Caractéristiques spécifiques</Title>
          {(() => {
            // Collecter TOUTES facets (nouvelles colonnes / spécifiques)
            const allFacets = product.facets && typeof product.facets === 'object' ? { ...product.facets } : {};
            const facetKeys = Object.keys(allFacets);
            if (facetKeys.length === 0) {
              return <Text type="secondary">Aucune caractéristique enregistrée pour ce produit.</Text>;
            }
            return (
              <Descriptions variant="outlined" column={2} size="small" style={{ marginTop: 8 }}>
                {facetKeys.map(fieldKey => {
                  const value = allFacets[fieldKey];
                  if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return null;
                  const fieldName = fieldKey
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .replace(/_/g, ' ')
                    .trim();
                  let displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                  return (
                    <Descriptions.Item key={fieldKey} label={cleanDisplay(fieldName)}>
                      <Text>{cleanDisplay(displayValue)}</Text>
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            );
          })()}

          {/* Documents */}
          <Divider />
          <Title level={4}>Documents</Title>
          <Descriptions variant="outlined" column={1} size="small">
            {product.documents?.datasheet ? (
              <Descriptions.Item label="Fiche technique">
                <a 
                  href={normalizeDocumentUrl(product.documents.datasheet)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  📄 Télécharger la fiche technique
                </a>
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Fiche technique">
                <Text type="secondary">Non disponible</Text>
              </Descriptions.Item>
            )}
            
            {product.documents?.manual ? (
              <Descriptions.Item label="Manuel d'utilisation">
                <a 
                  href={normalizeDocumentUrl(product.documents.manual)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  📖 Télécharger le manuel
                </a>
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Manuel d'utilisation">
                <Text type="secondary">Non disponible</Text>
              </Descriptions.Item>
            )}
            {product.documents?.certificate ? (
              <Descriptions.Item label="Certificat">
                <a
                  href={normalizeDocumentUrl(product.documents.certificate)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  🛡️ Télécharger le certificat
                </a>
              </Descriptions.Item>
            ) : null}
          
          </Descriptions>
          

        </div>
      )}
    </Modal>
  );
};

export default ProductDetailsModal;
